import { makeAutoObservable, runInAction } from 'mobx';

/** Contract for a single step in a bullet-tabs multi-step flow. */
export interface BulletTabStep {
  isValid: boolean;
  /** Finishes the flow when this is the last / completing step. */
  completeStep?(): Promise<void>;
  /** Clears step state when navigating back from this step. */
  resetStep?(): void;
}

export interface BulletTabsDelegateParams {
  getStepControllers: () => BulletTabStep[];
  /** Leaves the flow when Back is pressed on step 0 (e.g. return to group hub). */
  exitFromFirstStep: () => void;
}

export default class BulletTabsDelegate {
  private _currentStepIndex = 0;
  private _isActionLoading = false;

  private readonly _getStepControllers: () => BulletTabStep[];
  private readonly _exitFromFirstStep: () => void;

  public get currentStepIndex(): number {
    return this._currentStepIndex;
  }

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public get isCurrentStepValid(): boolean {
    return this._currentStepController?.isValid ?? false;
  }

  public get canCompleteStep(): boolean {
    return this.isCurrentStepValid && !this.isActionLoading;
  }

  private get _currentStepController(): BulletTabStep | undefined {
    return this._getStepControllers()[this._currentStepIndex];
  }

  private get _maxStepIndex(): number {
    return Math.max(this._getStepControllers().length - 1, 0);
  }

  private _setStepIndex(index: number): void {
    if (index >= 0 && index <= this._maxStepIndex) {
      this._currentStepIndex = index;
    }
  }

  private _setActionLoading(isLoading: boolean): void {
    this._isActionLoading = isLoading;
  }

  public navigateToPreviousStep(): void {
    if (this._currentStepIndex === 0) {
      this._exitFromFirstStep();
      return;
    }

    this._currentStepController?.resetStep?.();
    this._setStepIndex(this._currentStepIndex - 1);
  }

  public navigateToNextStep(): void {
    this._setStepIndex(this._currentStepIndex + 1);
  }

  public async completeFlow(): Promise<void> {
    const currentStep = this._currentStepController;
    if (!currentStep?.completeStep) {
      throw new Error('Current step cannot complete the flow');
    }

    this._setActionLoading(true);
    try {
      await currentStep.completeStep();
    } finally {
      runInAction(() => {
        this._setActionLoading(false);
      });
    }
  }

  constructor(params: BulletTabsDelegateParams) {
    this._getStepControllers = params.getStepControllers;
    this._exitFromFirstStep = params.exitFromFirstStep;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
